import axios from "axios";

export const SHOP_API_BASE_URL = "https://shop.nabhiverse.com/api/v1/";
export const SHOP_AUTH_API_BASE_URL = "https://shop.nabhiverse.com/api/v1/auth/";
export const XSRF_COOKIE_NAME = "XSRF-TOKEN";
export const SHOP_API_TOKEN_STORAGE_KEY = "shopApiToken";

export const SHOP_AUTH_ENDPOINTS = {
  sendOtp: `${SHOP_AUTH_API_BASE_URL}send_otp`,
  verifyOtp: `${SHOP_AUTH_API_BASE_URL}verify_mobile_otp`,
  register: `${SHOP_AUTH_API_BASE_URL}userregister`,
  ssoCode: `${SHOP_API_BASE_URL}sso/code`,
  logout: `${SHOP_AUTH_API_BASE_URL}logout`,
  checkLogin: `${SHOP_AUTH_API_BASE_URL}check-login`,
};

export const setXsrfTokenCookie = (token) => {
  if (!token || typeof document === "undefined") return;

  const secure = window.location?.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${XSRF_COOKIE_NAME}=${encodeURIComponent(
    token
  )}; path=/; max-age=31536000; SameSite=Lax${secure}`;
};

export const clearXsrfTokenCookie = () => {
  if (typeof document === "undefined") return;

  const secure = window.location?.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${XSRF_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax${secure}`;
};

export const getXsrfTokenCookie = () => {
  if (typeof document === "undefined") return "";

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${XSRF_COOKIE_NAME}=`));

  if (!cookie) return "";

  return decodeURIComponent(cookie.substring(XSRF_COOKIE_NAME.length + 1));
};

const getLocalStorageItem = (key) => {
  if (typeof localStorage === "undefined") return "";

  return localStorage.getItem(key) || "";
};

const getAuthResponseValue = (data = {}, key, fallbackKey) =>
  data?.[key] ||
  data?.[fallbackKey] ||
  data?.data?.[key] ||
  data?.data?.[fallbackKey] ||
  data?.auth?.[key] ||
  data?.auth?.[fallbackKey] ||
  "";

export const getShopApiTokenFromResponse = (data = {}) =>
  getAuthResponseValue(data, "token", "access_token");

const requestLogout = (apiToken) => {
  const dashboardUrl = getLocalStorageItem("dashboardUrl");
  const sessionToken = getLocalStorageItem("sessionToken");

  if (!apiToken && !dashboardUrl && !sessionToken) return Promise.resolve(null);

  const config = {
    params: {},
    headers: {},
    withCredentials: true,
  };

  if (dashboardUrl) {
    config.params.dashboardUrl = dashboardUrl;
  }

  if (sessionToken) {
    config.params.sessionToken = sessionToken;
  }

  if (apiToken) {
    config.headers.Authorization = `Bearer ${apiToken}`;
  }

  return axios.get(
    SHOP_AUTH_ENDPOINTS.logout,
    config
  );
};

const requestCheckLogin = (apiToken) => {
  const dashboardUrl = getLocalStorageItem("dashboardUrl");
  const sessionToken = getLocalStorageItem("sessionToken");

  const config = {
    params: {},
    headers: {},
    withCredentials: true,
  };

  if (dashboardUrl) {
    config.params.dashboardUrl = dashboardUrl;
  }

  if (sessionToken) {
    config.params.sessionToken = sessionToken;
  }

  if (apiToken) {
    config.headers.Authorization = `Bearer ${apiToken}`;
  }

  return axios.get(SHOP_AUTH_ENDPOINTS.checkLogin, config);
};

const clearLocalAuthState = ({ navigate, setIsLogin, setIsShowBtn } = {}) => {
  localStorage.removeItem("LoginData");
  localStorage.removeItem("Userid");
  localStorage.removeItem("mobile");
  localStorage.removeItem("sessionToken");
  localStorage.removeItem("dashboardUrl");
  localStorage.removeItem(SHOP_API_TOKEN_STORAGE_KEY);
  sessionStorage.clear();
  clearXsrfTokenCookie();
  setIsLogin?.("");
  setIsShowBtn?.(true);
  navigate?.("/");
};

const normalizeAuthUser = (user = {}) => {
  if (user.loginid || user.mobileNo || user.referralCode) {
    return {
      ...user,
      loginid: user.loginid || user.id,
    };
  }

  return {
    ...user,
    loginid: user.id,
    name: user.name || `${user.f_name || ""} ${user.l_name || ""}`.trim(),
    email: user.email || "",
    mobileNo: user.phone || "",
    referralCode: user.referral_code || "",
    imagePath: user.image_full_url?.path || user.image || null,
    image: user.image_full_url?.path || user.image || null,
    role: user.role || 1,
  };
};

export const storeAuthResponse = (data = {}) => {
  const loginData = normalizeAuthUser(data.user || {});
  const apiToken = getShopApiTokenFromResponse(data);

  if (apiToken) {
    setXsrfTokenCookie(apiToken);
    localStorage.setItem(SHOP_API_TOKEN_STORAGE_KEY, apiToken);
  }

  if (loginData.loginid) {
    localStorage.setItem("LoginData", JSON.stringify(loginData));
    localStorage.setItem("Userid", loginData.loginid);
  }

  const sessionToken = getAuthResponseValue(data, "sessionToken", "session_token");
  const dashboardUrl = getAuthResponseValue(data, "dashboardUrl", "dashboard_url");

  if (sessionToken) {
    localStorage.setItem("sessionToken", sessionToken);
  }

  if (dashboardUrl) {
    localStorage.setItem("dashboardUrl", dashboardUrl);
  }

  localStorage.removeItem("mobile");

  return loginData;
};

export const syncAuthFromShop = async ({ setIsLogin, setIsShowBtn } = {}) => {
  const apiToken = getXsrfTokenCookie() || getLocalStorageItem(SHOP_API_TOKEN_STORAGE_KEY);
  const hasAuthContext =
    apiToken ||
    getLocalStorageItem("dashboardUrl") ||
    getLocalStorageItem("sessionToken") ||
    getLocalStorageItem("LoginData") ||
    getLocalStorageItem("Userid");

  if (!hasAuthContext) {
    return null;
  }

  try {
    const response = await requestCheckLogin(apiToken);
    const data = response?.data || {};

    if (data.success === true && data.logged_in === true && data.user) {
      storeAuthResponse(data);

      setIsLogin?.(true);
      setIsShowBtn?.(false);
      return true;
    }

    await requestLogout(apiToken).catch(() => {});
    clearLocalAuthState({ setIsLogin, setIsShowBtn });
    return false;
  } catch (error) {
    const isUnauthorized =
      error?.response?.status === 401 ||
      error?.response?.data?.errors?.some?.((item) => item?.code === "auth-001");

    if (isUnauthorized) {
      await requestLogout(apiToken).catch(() => {});
      clearLocalAuthState({ setIsLogin, setIsShowBtn });
      return false;
    }

    return null;
  }
};

const showLogoutConfirmation = () =>
  new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(true);
      return;
    }

    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.inset = "0";
    overlay.style.zIndex = "99999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.background = "rgba(2, 3, 12, 0.78)";
    overlay.style.backdropFilter = "blur(6px)";

    const dialog = document.createElement("div");
    dialog.style.width = "min(420px, 100%)";
    dialog.style.border = "1px solid rgba(179, 139, 46, 0.35)";
    dialog.style.borderRadius = "12px";
    dialog.style.background =
      "radial-gradient(circle at 100% 0%, rgba(140, 38, 255, 0.18), transparent 36%), #11101c";
    dialog.style.boxShadow = "0 24px 70px rgba(0, 0, 0, 0.5)";
    dialog.style.color = "#ffffff";
    dialog.style.fontFamily = "Arial, sans-serif";
    dialog.style.padding = "24px";

    dialog.innerHTML = `
      <div style="display:flex;gap:14px;align-items:flex-start;">
        <div style="
          width:44px;
          height:44px;
          flex:0 0 auto;
          display:grid;
          place-items:center;
          border-radius:50%;
          background:rgba(140,38,255,0.16);
          border:1px solid rgba(215,93,255,0.42);
          color:#f5c453;
          font-size:22px;
          font-weight:900;
        ">!</div>
        <div style="min-width:0;">
          <h2 style="margin:0 0 8px;font-size:22px;line-height:1.2;font-weight:900;">Logout?</h2>
          <p style="margin:0;color:#aeb2c5;font-size:14px;line-height:1.5;">Are you sure you want to logout from Nabhiverse?</p>
        </div>
      </div>
      <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:24px;">
        <button type="button" data-logout-cancel style="
          min-width:96px;
          border:1px solid rgba(255,255,255,0.14);
          border-radius:8px;
          background:#1b1a2b;
          color:#ffffff;
          padding:10px 16px;
          font-size:14px;
          font-weight:800;
          cursor:pointer;
        ">Cancel</button>
        <button type="button" data-logout-confirm style="
          min-width:96px;
          border:0;
          border-radius:8px;
          background:linear-gradient(135deg,#5b12d6,#8c26ff);
          color:#ffffff;
          padding:10px 16px;
          font-size:14px;
          font-weight:900;
          cursor:pointer;
          box-shadow:0 10px 24px rgba(140,38,255,0.32);
        ">Logout</button>
      </div>
    `;

    const close = (value) => {
      overlay.remove();
      resolve(value);
    };

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    dialog.querySelector("[data-logout-cancel]")?.addEventListener("click", () => close(false));
    dialog.querySelector("[data-logout-confirm]")?.addEventListener("click", () => close(true));
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close(false);
    });
  });

export const logoutUser = async ({ navigate, setIsLogin, setIsShowBtn } = {}) => {
  const shouldLogout = await showLogoutConfirmation();

  if (!shouldLogout) {
    return false;
  }

  const apiToken = getXsrfTokenCookie() || getLocalStorageItem(SHOP_API_TOKEN_STORAGE_KEY);
  const hasRemoteLogoutContext =
    apiToken || getLocalStorageItem("dashboardUrl") || getLocalStorageItem("sessionToken");
  const logoutPopupWindow = hasRemoteLogoutContext ? openSsoPopupWindow() : null;

  try {
    const response = await requestLogout(apiToken);
    const shopLogoutUrl = getAuthResponseValue(
      response?.data || {},
      "shop_logout_url",
      "shopLogoutUrl"
    );

    if (!openLoginUrlInPopup(shopLogoutUrl, logoutPopupWindow)) {
      closeSsoPopupWindow(logoutPopupWindow);
    }
  } catch {
    closeSsoPopupWindow(logoutPopupWindow);
    // Continue local logout even if the remote session is already expired.
  }

  clearLocalAuthState({ navigate, setIsLogin, setIsShowBtn });

  return true;
};

export const isUserLoggedIn = () => {
  if (typeof localStorage === "undefined") return false;

  return Boolean(localStorage.getItem("Userid") && localStorage.getItem("LoginData"));
};

export const requireLogin = (setIsLoginShow) => {
  if (isUserLoggedIn()) return true;

  setIsLoginShow?.(true);
  return false;
};

const normalizeLoginUrl = (loginUrl) => {
  if (!loginUrl) return "";

  return loginUrl.replace(/\\\//g, "/");
};

export const openSsoPopupWindow = () => {
  if (typeof window === "undefined") return null;

  const width = 420;
  const height = 620;
  const screenLeft = window.screenLeft ?? window.screenX ?? 0;
  const screenTop = window.screenTop ?? window.screenY ?? 0;
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight || window.screen.height;
  const left = Math.round(screenLeft + Math.max((viewportWidth - width) / 2, 0));
  const top = Math.round(screenTop + Math.max((viewportHeight - height) / 2, 0));

  const popupWindow = window.open(
    "",
    "nabhiverse_sso_popup",
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`
  );

  if (popupWindow) {
    try {
      popupWindow.document.write(
        `<!doctype html>
        <html>
          <head>
            <title>Nabhiverse</title>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <style>
              * {
                box-sizing: border-box;
              }

              body {
                margin: 0;
                min-height: 100vh;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #070510;
                color: #ffffff;
                font-family: Arial, sans-serif;
              }

              .sso-loader {
                width: min(320px, calc(100vw - 32px));
                padding: 28px 24px;
                text-align: center;
                border: 1px solid rgba(179, 139, 46, 0.35);
                border-radius: 14px;
                background: #11101c;
                box-shadow: 0 18px 60px rgba(0, 0, 0, 0.35);
              }

              .brand-mark {
                width: 64px;
                height: 64px;
                margin: 0 auto 18px;
                display: grid;
                place-items: center;
                border-radius: 50%;
                background: #17142a;
                color: #f5c453;
                font-size: 30px;
                font-weight: 800;
                border: 1px solid rgba(245, 196, 83, 0.45);
              }

              .spinner {
                width: 42px;
                height: 42px;
                margin: 0 auto 18px;
                border-radius: 50%;
                border: 4px solid rgba(255, 255, 255, 0.14);
                border-top-color: #8b1cff;
                animation: spin 0.8s linear infinite;
              }

              h1 {
                margin: 0 0 8px;
                font-size: 22px;
                line-height: 1.2;
              }

              p {
                margin: 0;
                color: rgba(255, 255, 255, 0.7);
                font-size: 14px;
                line-height: 1.5;
              }

              @keyframes spin {
                to {
                  transform: rotate(360deg);
                }
              }
            </style>
          </head>
          <body>
            <main class="sso-loader">
              <div class="brand-mark">N</div>
              <div class="spinner" aria-hidden="true"></div>
              <h1>Connecting...</h1>
              <p>Please wait while we complete secure login.</p>
            </main>
          </body>
        </html>`
      );
      popupWindow.document.close();
    } catch {
      // Popup is still usable even if the browser blocks document writes.
    }
  }

  return popupWindow;
};

export const closeSsoPopupWindow = (popupWindow) => {
  if (!popupWindow) return;

  try {
    if (!popupWindow.closed) {
      popupWindow.close();
    }
  } catch {
    // Ignore cross-origin close checks.
  }
};

const openLoginUrlInPopup = (loginUrl, popupWindow) => {
  const normalizedLoginUrl = normalizeLoginUrl(loginUrl);

  if (!normalizedLoginUrl) {
    return false;
  }

  if (popupWindow && !popupWindow.closed) {
    popupWindow.location.href = normalizedLoginUrl;

    setTimeout(() => {
      closeSsoPopupWindow(popupWindow);
    }, 1200);

    return true;
  }

  const fallbackPopup = openSsoPopupWindow();

  if (!fallbackPopup) {
    return false;
  }

  fallbackPopup.location.href = normalizedLoginUrl;
  setTimeout(() => {
    closeSsoPopupWindow(fallbackPopup);
  }, 1200);

  return true;
};

export const requestSsoCode = async (apiToken, popupWindow = null) => {
  if (!apiToken) return Promise.resolve(null);

  const response = await axios.post(
    SHOP_AUTH_ENDPOINTS.ssoCode,
    {},
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    }
  );

  openLoginUrlInPopup(response.data?.login_url, popupWindow);

  return response;
};
